import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { db, serverTimestamp } from '../firebase.ts';
import type { CurrentUser } from '../firebase.ts';
import { blogPosts } from '../constants.tsx';

interface CreatePostPageProps {
    currentUser: CurrentUser;
    onPostCreated: (postId: string) => void;
}

const MEDIA_API = 'https://news-writer.itsmybadsoul.workers.dev';

const AUTHOR_NAME = 'سارة';
const AUTHOR_TITLE = 'محللة أسواق — السوق السعودي والأمريكي';

const SECTOR_OPTIONS = [
    { key: 'banks', label: 'البنوك' },
    { key: 'energy', label: 'الطاقة والبتروكيماويات' },
    { key: 'realestate', label: 'العقار' },
    { key: 'telecom', label: 'الاتصالات' },
    { key: 'industry', label: 'الصناعة' },
    { key: 'retail', label: 'التجزئة والاستهلاك' },
    { key: 'healthcare', label: 'الصحة' },
    { key: 'market', label: 'السوق كله' },
];

const KIND_LABELS: Record<string, string> = {
    stock: 'صورة سهم',
    sector: 'صورة قطاع',
    logo: 'شعار شركة',
    general: 'صورة عامة',
};

const DIRECTION_LABELS: Record<string, string> = {
    up: 'صعود',
    down: 'هبوط',
    neutral: 'محايد',
};

interface MediaImage {
    key: string;
    url: string;
    label: string;
    kind: string;
    codes: string[];
    sectors: string[];
    direction: string;
    createdAt?: string;
}

interface CompanyRow {
    code: string;
    name: string;
    legal?: string;
    sector?: string;
}

const sectorLabel = (key: string) => SECTOR_OPTIONS.find((s) => s.key === key)?.label || key;

interface ArticleRow {
    id: string;
    slug: string;
    title: string;
    image?: string;
    date?: string;
    type?: string;
    source: 'feed' | 'static' | 'manual' | 'summary';
}

const SOURCE_LABELS: Record<ArticleRow['source'], string> = {
    feed: 'مقال آلي',
    static: 'مقال قديم',
    manual: 'مقال يدوي',
    summary: 'ملخص يومي',
};

const ENGINE_LABELS: Record<string, string> = {
    premarket: 'قبل الافتتاح',
    liquidity: 'خريطة السيولة',
    close: 'تحليل الجلسة',
    news: 'أخبار الشركات',
};

const ENGINE_WINDOWS: Record<string, string> = {
    premarket: '09:20 – 09:58',
    liquidity: '12:00 – 13:30',
    close: '15:30 – 16:40',
    news: '10:00 – 15:20 بفاصل 50 دقيقة',
};

interface EngineConfig {
    enabled: boolean;
    maxPerDay: number;
    newsPerDay: number;
    newsGapMin: number;
    disabledTypes: string[];
    instructions: string;
    signWithSara: boolean;
}

interface EngineData {
    config: EngineConfig;
    today: string;
    state: { day?: string; counts?: Record<string, number>; lastRunAt?: number; lastPublishAt?: number; lastError?: { at?: string; errors?: string[] } | null };
    total: number;
    latest: { slug: string; title: string; date?: string; type?: string }[];
    usage: { month: string; thisMonth: { costUsd: number; calls?: number }; total: { costUsd: number } };
}

interface EditState {
    key: string;
    label: string;
    kind: string;
    direction: string;
    codes: string[];
    sectors: string[];
}

const CreatePostPage: React.FC<CreatePostPageProps> = ({ currentUser, onPostCreated }) => {
    const { t } = useTranslation();
    const [tab, setTab] = useState<'post' | 'media' | 'articles' | 'engine'>('post');

    // --- المقال ---
    const [title, setTitle] = useState('');
    const [excerpt, setExcerpt] = useState('');
    const [content, setContent] = useState('');
    const [imageUrl, setImageUrl] = useState('');
    const [tags, setTags] = useState('');
    const [status, setStatus] = useState<'idle' | 'submitting' | 'error'>('idle');
    const [errorMessage, setErrorMessage] = useState('');

    // --- المكتبة ---
    const [images, setImages] = useState<MediaImage[]>([]);
    const [companies, setCompanies] = useState<CompanyRow[]>([]);
    const [libraryLoading, setLibraryLoading] = useState(true);
    const [libraryError, setLibraryError] = useState('');
    const [files, setFiles] = useState<File[]>([]);
    const [kind, setKind] = useState('stock');
    const [direction, setDirection] = useState('neutral');
    const [label, setLabel] = useState('');
    const [selectedCodes, setSelectedCodes] = useState<string[]>([]);
    const [selectedSectors, setSelectedSectors] = useState<string[]>([]);
    const [codeQuery, setCodeQuery] = useState('');
    const [uploading, setUploading] = useState(false);
    const [uploadMsg, setUploadMsg] = useState('');
    const [uploadError, setUploadError] = useState('');
    const [filter, setFilter] = useState('');
    const [kindFilter, setKindFilter] = useState('all');
    const [edit, setEdit] = useState<EditState | null>(null);
    const [editQuery, setEditQuery] = useState('');
    const [savingEdit, setSavingEdit] = useState(false);
    const [editError, setEditError] = useState('');

    // --- المقالات والصور ---
    const [articles, setArticles] = useState<ArticleRow[]>([]);
    const [articlesLoading, setArticlesLoading] = useState(false);
    const [articlesError, setArticlesError] = useState('');
    const [articleQuery, setArticleQuery] = useState('');
    const [coverTarget, setCoverTarget] = useState<string | null>(null);
    const [coverUrl, setCoverUrl] = useState('');
    const [coverBusy, setCoverBusy] = useState('');
    const [coverMsg, setCoverMsg] = useState('');

    // --- محرك الأخبار ---
    const [engine, setEngine] = useState<EngineData | null>(null);
    const [engineLoading, setEngineLoading] = useState(false);
    const [engineError, setEngineError] = useState('');
    const [engineForm, setEngineForm] = useState<EngineConfig | null>(null);
    const [engineSaving, setEngineSaving] = useState(false);
    const [engineMsg, setEngineMsg] = useState('');
    const [engineRunning, setEngineRunning] = useState('');

    const token = useCallback(async () => {
        const user = currentUser?.auth;
        if (!user || typeof user.getIdToken !== 'function') throw new Error('الجلسة منتهية، سجّل الدخول من جديد');
        return user.getIdToken();
    }, [currentUser]);

    const loadLibrary = useCallback(async () => {
        setLibraryLoading(true);
        try {
            const res = await fetch(`${MEDIA_API}/media.json?t=${Date.now()}`);
            const body = await res.json();
            setImages(Array.isArray(body.images) ? body.images : []);
            setLibraryError('');
        } catch (e) {
            setLibraryError('تعذر تحميل مكتبة الصور');
        } finally {
            setLibraryLoading(false);
        }
    }, []);

    useEffect(() => {
        loadLibrary();
        fetch(`${MEDIA_API}/companies.json`)
            .then((r) => r.json())
            .then((b) => setCompanies(Array.isArray(b.companies) ? b.companies : []))
            .catch(() => setCompanies([]));
    }, [loadLibrary]);

    const previews = useMemo(
        () => files.map((file) => ({ file, url: URL.createObjectURL(file) })),
        [files],
    );

    useEffect(() => () => previews.forEach((p) => URL.revokeObjectURL(p.url)), [previews]);

    useEffect(() => {
        if (tab !== 'media') return;
        const onPaste = (event: ClipboardEvent) => {
            const items = Array.from(event.clipboardData?.items || []);
            const pasted: File[] = [];
            for (const item of items) {
                if (item.kind !== 'file' || !item.type.startsWith('image/')) continue;
                const blob = item.getAsFile();
                if (!blob) continue;
                const ext = (blob.type.split('/')[1] || 'png').replace('jpeg', 'jpg');
                pasted.push(new File([blob], `pasted-${Date.now()}-${pasted.length}.${ext}`, { type: blob.type }));
            }
            if (!pasted.length) return;
            event.preventDefault();
            setFiles((prev) => [...prev, ...pasted].slice(0, 20));
            setUploadError('');
            setUploadMsg('');
        };
        document.addEventListener('paste', onPaste);
        return () => document.removeEventListener('paste', onPaste);
    }, [tab]);

    const companyNames = useMemo(() => {
        const map = new Map<string, string>();
        companies.forEach((c) => map.set(String(c.code), String(c.name || c.code)));
        return map;
    }, [companies]);

    const codeLabel = (code: string) => (code === 'TASI' ? 'المؤشر' : companyNames.get(String(code)) || String(code));

    const tagLabel = (code: string) => (code === 'TASI' ? 'تاسي (المؤشر)' : `${codeLabel(code)} (${code})`);

    const filteredCompanies = useMemo(() => {
        const q = codeQuery.trim().toLowerCase();
        const base = q
            ? companies.filter((c) => c.code.includes(q) || String(c.name || '').toLowerCase().includes(q) || String(c.legal || '').toLowerCase().includes(q))
            : companies;
        return base.slice(0, 60);
    }, [companies, codeQuery]);

    const visibleImages = useMemo(() => {
        const q = filter.trim().toLowerCase();
        return images.filter((img) => {
            if (kindFilter !== 'all' && img.kind !== kindFilter) return false;
            if (!q) return true;
            const hay = `${img.label} ${(img.codes || []).join(' ')} ${(img.sectors || []).join(' ')}`.toLowerCase();
            return hay.includes(q);
        });
    }, [images, filter, kindFilter]);

    const toggle = (list: string[], value: string, setter: (v: string[]) => void) => {
        setter(list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    };

    const editCompanies = useMemo(() => {
        const q = editQuery.trim().toLowerCase();
        const base = q
            ? companies.filter((c) => c.code.includes(q) || String(c.name || '').toLowerCase().includes(q) || String(c.legal || '').toLowerCase().includes(q))
            : companies;
        return base.slice(0, 12);
    }, [companies, editQuery]);

    const visibleArticles = useMemo(() => {
        const q = articleQuery.trim().toLowerCase();
        if (!q) return articles;
        return articles.filter((a) => `${a.title} ${a.slug}`.toLowerCase().includes(q));
    }, [articles, articleQuery]);

    const loadArticles = useCallback(async () => {
        setArticlesLoading(true);
        setArticlesError('');
        const rows: ArticleRow[] = [];
        const covers: Record<string, string> = {};
        try {
            const [feedRes, coversRes] = await Promise.allSettled([
                fetch(`${MEDIA_API}/articles.json?t=${Date.now()}`),
                fetch(`${MEDIA_API}/covers.json?t=${Date.now()}`),
            ]);
            if (coversRes.status === 'fulfilled' && coversRes.value.ok) {
                const c = await coversRes.value.json().catch(() => ({} as any));
                if (c && c.covers && typeof c.covers === 'object') Object.assign(covers, c.covers);
            }
            if (feedRes.status === 'fulfilled' && feedRes.value.ok) {
                const body = await feedRes.value.json().catch(() => ({} as any));
                if (Array.isArray(body.articles)) {
                    body.articles.forEach((a: any) => {
                        if (!a || !a.slug || !a.title) return;
                        rows.push({ id: a.slug, slug: a.slug, title: a.title, image: a.image, date: a.date, type: a.type, source: 'feed' });
                    });
                }
            }
            const legacyCovers = ['/images/news/telecom.jpg', '/images/news/energy.jpg', '/images/news/market.jpg'];
            blogPosts.forEach((p: any, i: number) => {
                const slug = p.slug || p.id;
                if (!slug) return;
                const fallback = legacyCovers[i % legacyCovers.length];
                const base = /picsum\.photos/.test(String(p.imageUrl || '')) ? fallback : p.imageUrl;
                rows.push({ id: slug, slug, title: t(p.titleKey), image: covers[slug] || base, date: p.date, type: 'static', source: 'static' });
            });
            const newest = rows.filter((r) => r.source === 'feed' && r.date).map((r) => String(r.date).slice(0, 10)).sort().pop();
            if (newest) {
                const slug = `market-summary-${newest}`;
                rows.push({
                    id: slug, slug, title: `ملخص السوق السعودي — ${newest}`,
                    image: covers[slug] || '/images/news/market.jpg', date: newest, type: 'summary', source: 'summary',
                });
            }
            try {
                const snap = await db.collection('posts').orderBy('createdAt', 'desc').limit(60).get();
                snap.docs.forEach((doc) => {
                    const d: any = doc.data() || {};
                    const created = d.createdAt && typeof d.createdAt.toDate === 'function' ? d.createdAt.toDate() : null;
                    rows.push({
                        id: doc.id,
                        slug: doc.id,
                        title: d.title || 'مقال بدون عنوان',
                        image: d.imageUrl,
                        date: created ? created.toISOString().slice(0, 10) : '',
                        type: 'manual',
                        source: 'manual',
                    });
                });
            } catch (e) { /* المقالات اليدوية اختيارية */ }
        } catch (e) {
            setArticlesError('تعذر تحميل المقالات');
        }
        rows.sort((a, b) => String(b.date || '').localeCompare(String(a.date || '')));
        setArticles(rows);
        setArticlesLoading(false);
    }, [t]);

    useEffect(() => {
        if (tab !== 'articles') return;
        loadArticles();
    }, [tab, loadArticles]);

    const loadEngine = useCallback(async () => {
        setEngineLoading(true);
        setEngineError('');
        try {
            const idToken = await token();
            const res = await fetch(`${MEDIA_API}/engine`, {
                headers: { authorization: `Bearer ${idToken}` },
                cache: 'no-store',
            });
            const body = await res.json().catch(() => ({} as any));
            if (!res.ok) throw new Error(body.error || `خطأ ${res.status}`);
            setEngine(body);
            setEngineForm(body.config);
        } catch (err: any) {
            setEngineError(err?.message || 'تعذر قراءة حالة المحرك');
        } finally {
            setEngineLoading(false);
        }
    }, [token]);

    useEffect(() => {
        if (tab !== 'engine') return;
        if (!engine) loadEngine();
    }, [tab, engine, loadEngine]);

    const saveEngine = async (patch: Partial<EngineConfig> = {}) => {
        if (!engineForm) return;
        setEngineSaving(true);
        setEngineMsg('');
        try {
            const idToken = await token();
            const res = await fetch(`${MEDIA_API}/engine`, {
                method: 'PATCH',
                headers: { 'content-type': 'application/json', authorization: `Bearer ${idToken}` },
                body: JSON.stringify({ ...engineForm, ...patch }),
            });
            const body = await res.json().catch(() => ({} as any));
            if (!res.ok) throw new Error(body.error || `فشل الحفظ (${res.status})`);
            setEngine((prev) => (prev ? { ...prev, config: body.config } : prev));
            setEngineForm(body.config);
            setEngineMsg('تم حفظ إعدادات المحرك ✅');
        } catch (err: any) {
            setEngineMsg(err?.message || 'فشل الحفظ');
        } finally {
            setEngineSaving(false);
        }
    };

    const runEngine = async (type?: string) => {
        const label = type ? ENGINE_LABELS[type] || type : 'كل الأنواع';
        if (!window.confirm(`تشغيل المحرك الآن (${label})؟ هيستهلك من رصيد الموديل وينشر مقالات جديدة.`)) return;
        setEngineRunning(type || 'all');
        setEngineMsg('');
        try {
            const idToken = await token();
            const qs = new URLSearchParams({ force: '1' });
            if (type) qs.set('type', type);
            const res = await fetch(`${MEDIA_API}/run?${qs.toString()}`, {
                headers: { authorization: `Bearer ${idToken}` },
            });
            const body = await res.json().catch(() => ({} as any));
            if (!res.ok) throw new Error(body.error || `فشل التشغيل (${res.status})`);
            const generated = Array.isArray(body.generated) ? body.generated : [];
            const errors = Array.isArray(body.errors) ? body.errors : [];
            if (generated.length) setEngineMsg(`تم النشر: ${generated.map((g: any) => g.slug || g.title).join(' · ')}`);
            else if (errors.length) setEngineMsg(`المحرك رجع أخطاء: ${errors.join(' | ')}`);
            else setEngineMsg('تم التشغيل — مفيش مقال جديد (النوع اتنشر قبل كده أو مفيش خبر مناسب).');
            await loadEngine();
        } catch (err: any) {
            setEngineMsg(err?.message || 'فشل التشغيل');
        } finally {
            setEngineRunning('');
        }
    };

    const toggleEngineType = (value: string) => {
        setEngineForm((prev) => {
            if (!prev) return prev;
            const list = prev.disabledTypes.includes(value)
                ? prev.disabledTypes.filter((v) => v !== value)
                : [...prev.disabledTypes, value];
            return { ...prev, disabledTypes: list };
        });
    };

    const startEdit = (img: MediaImage) => {
        setEdit({
            key: img.key,
            label: img.label || '',
            kind: img.kind || 'general',
            direction: img.direction || 'neutral',
            codes: [...(img.codes || [])],
            sectors: [...(img.sectors || [])],
        });
        setEditQuery('');
        setEditError('');
    };

    const saveEdit = async () => {
        if (!edit) return;
        if (!edit.codes.length && !edit.sectors.length) { setEditError('حدد كود سهم أو قطاع على الأقل'); return; }
        setSavingEdit(true);
        setEditError('');
        try {
            const idToken = await token();
            const res = await fetch(`${MEDIA_API}/media`, {
                method: 'PATCH',
                headers: { 'content-type': 'application/json', authorization: `Bearer ${idToken}` },
                body: JSON.stringify({
                    key: edit.key,
                    label: edit.label,
                    kind: edit.kind,
                    direction: edit.direction,
                    codes: edit.codes,
                    sectors: edit.sectors,
                }),
            });
            const body = await res.json().catch(() => ({} as any));
            if (!res.ok) throw new Error(body.error || `فشل الحفظ (${res.status})`);
            setImages((prev) => prev.map((i) => (i.key === edit.key ? { ...i, ...body.image } : i)));
            setEdit(null);
        } catch (err: any) {
            setEditError(err?.message || 'فشل الحفظ');
        } finally {
            setSavingEdit(false);
        }
    };

    const applyCover = async (row: ArticleRow, url: string) => {
        if (!url.trim()) { setCoverMsg('حدد صورة أولًا'); return; }
        setCoverBusy(row.id);
        setCoverMsg('');
        try {
            const image = url.trim();
            if (row.source === 'manual') {
                await db.collection('posts').doc(row.id).update({ imageUrl: image });
            } else {
                const idToken = await token();
                const endpoint = row.source === 'feed' ? 'article/cover' : 'post-cover';
                const res = await fetch(`${MEDIA_API}/${endpoint}`, {
                    method: 'POST',
                    headers: { 'content-type': 'application/json', authorization: `Bearer ${idToken}` },
                    body: JSON.stringify({ slug: row.slug, image }),
                });
                const body = await res.json().catch(() => ({} as any));
                if (!res.ok) throw new Error(body.error || `فشل الحفظ (${res.status})`);
            }
            setArticles((prev) => prev.map((a) => (a.id === row.id ? { ...a, image } : a)));
            setCoverMsg(row.source === 'manual' ? 'تم تحديث صورة المقال اليدوي ✅' : 'تم ربط الصورة بالمقال — الموقع هيتحدّث خلال دقيقة');
            setCoverTarget(null);
            setCoverUrl('');
        } catch (err: any) {
            setCoverMsg(err?.message || 'فشل الحفظ');
        } finally {
            setCoverBusy('');
        }
    };

    const handleUpload = async (e: React.FormEvent) => {
        e.preventDefault();
        setUploadError('');
        setUploadMsg('');
        if (!files.length) { setUploadError('اختر صورة أولًا'); return; }
        if (!selectedCodes.length && !selectedSectors.length) { setUploadError('حدد كود سهم أو قطاع واحد على الأقل'); return; }
        setUploading(true);
        let done = 0;
        try {
            const idToken = await token();
            for (const file of files) {
                const fd = new FormData();
                fd.append('file', file);
                fd.append('kind', kind);
                fd.append('direction', direction);
                fd.append('label', label.trim());
                fd.append('codes', selectedCodes.join(','));
                fd.append('sectors', selectedSectors.join(','));
                const res = await fetch(`${MEDIA_API}/media/upload`, {
                    method: 'POST',
                    headers: { authorization: `Bearer ${idToken}` },
                    body: fd,
                });
                const body = await res.json().catch(() => ({} as any));
                if (!res.ok) throw new Error(body.error || `فشل الرفع (${res.status})`);
                done += 1;
            }
            setUploadMsg(`تم رفع ${done} صورة بنجاح`);
            setFiles([]);
            setLabel('');
            await loadLibrary();
        } catch (err: any) {
            setUploadError(err?.message || 'فشل الرفع');
        } finally {
            setUploading(false);
        }
    };

    const handleDelete = async (key: string) => {
        if (!window.confirm('متأكد إنك عاوز تحذف الصورة دي؟')) return;
        try {
            const idToken = await token();
            const res = await fetch(`${MEDIA_API}/media?key=${encodeURIComponent(key)}`, {
                method: 'DELETE',
                headers: { authorization: `Bearer ${idToken}` },
            });
            if (!res.ok) throw new Error('delete failed');
            setImages((prev) => prev.filter((i) => i.key !== key));
        } catch (e) {
            setUploadError('تعذر حذف الصورة');
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!title.trim() || !content.trim() || !excerpt.trim()) {
            setStatus('error');
            setErrorMessage('العنوان والملخص والنص مطلوبين.');
            return;
        }
        setStatus('submitting');
        setErrorMessage('');
        try {
            const postsCollection = db.collection('posts');
            const docRef = await postsCollection.add({
                title: title.trim(),
                excerpt: excerpt.trim(),
                content: content.trim(),
                imageUrl: imageUrl.trim() || '/images/news/market.jpg',
                tags: tags.split(',').map((tag) => tag.trim()).filter(Boolean),
                authorName: AUTHOR_NAME,
                authorTitle: AUTHOR_TITLE,
                authorId: currentUser.auth.uid,
                createdAt: serverTimestamp(),
            });
            onPostCreated(docRef.id);
        } catch (error) {
            setStatus('error');
            setErrorMessage('حصل خطأ أثناء النشر. جرّب تاني.');
        } finally {
            if (status !== 'error') setStatus('idle');
        }
    };

    const thresholdNote = direction === 'up'
        ? 'هتستخدم في المقالات اللي سهمها صاعد.'
        : direction === 'down'
            ? 'هتستخدم في المقالات اللي سهمها هابط.'
            : 'تصلح للصعود والهبوط (محايدة).';

    const coverArticle = articles.find((a) => a.id === coverTarget) || null;

    const fmtWhen = (v?: string | number) => {
        if (!v) return '—';
        const d = new Date(v);
        if (Number.isNaN(d.getTime())) return '—';
        return d.toLocaleString('ar-SA', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
    };

    return (
        <section className="pt-32 lg:pt-36 xl:pt-40 pb-20 lg:pb-24">
            <div className="max-w-5xl mx-auto px-4 sm:px-8 xl:px-0">
                <div className="text-center mb-10">
                    <h1 className="text-white text-3xl font-extrabold sm:text-5xl">استوديو المحتوى</h1>
                    <p className="max-w-3xl mx-auto mt-4 font-medium text-white/70">
                        اكتب مقالًا جديدًا، أو ارفع صور الأسهم والقطاعات واربطها بالأكواد عشان المقالات تستخدمها تلقائيًا.
                    </p>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-3 mb-8">
                    <button type="button" onClick={() => setTab('post')} className={`rounded-lg py-2.5 px-6 font-medium transition ${tab === 'post' ? 'bg-white text-[#100624]' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>مقال جديد</button>
                    <button type="button" onClick={() => setTab('media')} className={`rounded-lg py-2.5 px-6 font-medium transition ${tab === 'media' ? 'bg-white text-[#100624]' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>مكتبة الصور {images.length ? `(${images.length})` : ''}</button>
                    <button type="button" onClick={() => setTab('articles')} className={`rounded-lg py-2.5 px-6 font-medium transition ${tab === 'articles' ? 'bg-white text-[#100624]' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>صور المقالات {articles.length ? `(${articles.length})` : ''}</button>
                    <button type="button" onClick={() => setTab('engine')} className={`rounded-lg py-2.5 px-6 font-medium transition ${tab === 'engine' ? 'bg-white text-[#100624]' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>محرك الأخبار</button>
                </div>

                {tab === 'post' && (
                    <div className="bg-[#100624] p-8 rounded-2xl border border-white/10 shadow-lg">
                        <form onSubmit={handleSubmit} className="w-full space-y-6">
                            <div>
                                <label htmlFor="title" className="text-white mb-2 block font-medium">عنوان المقال</label>
                                <input id="title" type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="اكتب عنوان المقال" className="auth-input w-full rounded-lg p-3 outline-none transition" required />
                            </div>
                            <div>
                                <label htmlFor="excerpt" className="text-white mb-2 block font-medium">ملخص قصير</label>
                                <input id="excerpt" type="text" value={excerpt} onChange={(e) => setExcerpt(e.target.value)} placeholder="سطر أو سطرين يظهروا في الكروت" className="auth-input w-full rounded-lg p-3 outline-none transition" required />
                            </div>
                            <div>
                                <label htmlFor="content" className="text-white mb-2 block font-medium">نص المقال</label>
                                <textarea id="content" value={content} onChange={(e) => setContent(e.target.value)} placeholder="اكتب المقال هنا... كل سطر جديد يبقى فقرة." rows={12} className="auth-input w-full rounded-lg p-3 outline-none transition" required></textarea>
                            </div>

                            <div>
                                <label className="text-white mb-2 block font-medium">صورة المقال</label>
                                <input id="imageUrl" type="url" value={imageUrl} onChange={(e) => setImageUrl(e.target.value)} placeholder="أو الصق رابط صورة" className="auth-input w-full rounded-lg p-3 outline-none transition" />
                                <p className="text-xs text-white/50 mt-2">لو مختارتش صورة، هنستخدم صورة السوق الافتراضية. تقدر تختار من مكتبة الصور تحت.</p>
                                {images.length > 0 && (
                                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-4">
                                        {images.slice(0, 10).map((img) => (
                                            <button
                                                type="button"
                                                key={img.key}
                                                onClick={() => setImageUrl(img.url)}
                                                className={`rounded-lg overflow-hidden border-2 transition ${imageUrl === img.url ? 'border-purple-400' : 'border-white/10 hover:border-white/30'}`}
                                                title={img.label}
                                            >
                                                <img src={img.url} alt={img.label} className="w-full h-16 object-cover" />
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label htmlFor="tags" className="text-white mb-2 block font-medium">وسوم</label>
                                <input id="tags" type="text" value={tags} onChange={(e) => setTags(e.target.value)} placeholder="تحليل, أرباح, طاقة" className="auth-input w-full rounded-lg p-3 outline-none transition" />
                                <p className="text-xs text-white/50 mt-2">افصل بينها بفاصلة.</p>
                            </div>

                            <p className="text-xs text-white/50">النشر بيتم باسم <span className="text-white/80">{AUTHOR_NAME}</span> — {AUTHOR_TITLE}.</p>

                            <div className="text-center pt-2">
                                <button type="submit" disabled={status === 'submitting'} className="hero-button w-full sm:w-auto inline-flex justify-center rounded-lg py-3 px-10 text-white font-medium ease-in duration-300 hover:opacity-80 disabled:opacity-50 disabled:cursor-wait">
                                    {status === 'submitting' ? 'جاري النشر...' : 'نشر المقال'}
                                </button>
                                {status === 'error' && <p className="text-red-400 mt-4">{errorMessage}</p>}
                            </div>
                        </form>
                    </div>
                )}

                {tab === 'media' && (
                    <div className="space-y-8">
                        <div className="bg-[#100624] p-6 sm:p-8 rounded-2xl border border-white/10 shadow-lg">
                            <h2 className="text-white text-xl font-bold mb-1">رفع صور جديدة</h2>
                            <p className="text-sm text-white/60 mb-6">ارفع صورة السهم أو القطاع، وحدد الكود والاتجاه، والمقالات هتستخدمها تلقائيًا.</p>
                            <form onSubmit={handleUpload} className="space-y-5">
                                <div>
                                    <label className="text-white mb-2 block font-medium text-sm">الصور</label>
                                    <input
                                        type="file"
                                        accept="image/jpeg,image/png,image/webp,image/gif"
                                        multiple
                                        onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 20))}
                                        className="auth-input w-full rounded-lg p-3 outline-none transition text-sm file:me-3 file:rounded-md file:border-0 file:bg-white/10 file:px-3 file:py-1.5 file:text-white"
                                    />
                                    <p className="text-xs text-white/50 mt-2">
                                        أو اضغط <span className="text-white/80">Ctrl + V</span> هنا للصق صورة أو سكرين شوت من الحافظة — تقدر تلصق كذا صورة ورا بعض.
                                    </p>
                                    {files.length > 0 && <p className="text-xs text-white/50 mt-1">{files.length} صورة مختارة — أقصى حجم 8 ميجا للصورة، و20 صورة في المرة.</p>}
                                    {previews.length > 0 && (
                                        <div className="grid grid-cols-3 sm:grid-cols-5 gap-3 mt-3">
                                            {previews.map((p, i) => (
                                                <div key={p.url} className="relative rounded-lg overflow-hidden border border-white/10">
                                                    <img src={p.url} alt={p.file.name} className="w-full h-20 object-cover" />
                                                    <button
                                                        type="button"
                                                        onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                                                        className="absolute top-1 end-1 w-5 h-5 rounded-full bg-black/70 text-white text-[11px] leading-none"
                                                        title="إزالة"
                                                    >
                                                        ✕
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <div>
                                        <label className="text-white mb-2 block font-medium text-sm">نوع الصورة</label>
                                        <select value={kind} onChange={(e) => setKind(e.target.value)} className="auth-input w-full rounded-lg p-3 outline-none transition">
                                            <option value="stock">صورة سهم / شركة</option>
                                            <option value="logo">شعار شركة</option>
                                            <option value="sector">صورة قطاع</option>
                                            <option value="general">صورة عامة</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="text-white mb-2 block font-medium text-sm">الوصف (اختياري)</label>
                                        <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="مثال: المراعي — مصنع" className="auth-input w-full rounded-lg p-3 outline-none transition" />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-white mb-2 block font-medium text-sm">الاتجاه</label>
                                    <div className="flex gap-2">
                                        {(['up', 'down', 'neutral'] as const).map((d) => (
                                            <button key={d} type="button" onClick={() => setDirection(d)} className={`rounded-lg py-2 px-4 text-sm font-medium transition ${direction === d ? 'bg-white text-[#100624]' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>
                                                {direction === d ? '✓ ' : ''}{DIRECTION_LABELS[d]}
                                            </button>
                                        ))}
                                    </div>
                                    <p className="text-xs text-white/50 mt-2">{thresholdNote}</p>
                                    <p className="text-xs text-white/40 mt-1">لو رفعت أكتر من صورة لنفس السهم ونفس الاتجاه، المحرك هيستخدم أحدث صورة، والباقي يفضل بدائل.</p>
                                </div>

                                <div>
                                    <label className="text-white mb-2 block font-medium text-sm">أكواد الأسهم</label>
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        <button
                                            type="button"
                                            onClick={() => toggle(selectedCodes, 'TASI', setSelectedCodes)}
                                            className={`text-xs rounded-full px-3 py-1.5 border transition ${selectedCodes.includes('TASI') ? 'bg-purple-500/20 border-purple-400/40 text-white' : 'bg-white/5 border-white/10 text-white/60 hover:text-white'}`}
                                        >
                                            تاسي (المؤشر)
                                        </button>
                                        <span className="text-[11px] text-white/40 self-center">اختَرها لصورة تاسي والسوق كله</span>
                                    </div>
                                    <input type="text" value={codeQuery} onChange={(e) => setCodeQuery(e.target.value)} placeholder="ابحث بالاسم أو الكود..." className="auth-input w-full rounded-lg p-3 outline-none transition" />
                                    {selectedCodes.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-3">
                                            {selectedCodes.map((code) => (
                                                <button key={code} type="button" onClick={() => toggle(selectedCodes, code, setSelectedCodes)} className="text-xs rounded-full px-3 py-1.5 bg-purple-500/20 border border-purple-400/40 text-white">
                                                    {tagLabel(code)} ✕
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                    <div className="mt-3 max-h-52 overflow-y-auto rounded-lg border border-white/10 divide-y divide-white/5">
                                        {filteredCompanies.map((c) => (
                                            <button key={c.code} type="button" onClick={() => toggle(selectedCodes, c.code, setSelectedCodes)} className={`w-full text-start px-3 py-2 text-sm transition hover:bg-white/5 ${selectedCodes.includes(c.code) ? 'bg-purple-500/10 text-white' : 'text-white/70'}`}>
                                                <span className="text-white/90">{c.name}</span> <span className="text-white/40">({c.code})</span>
                                                {c.sector && <span className="text-white/30 text-xs"> — {sectorLabel(c.sector)}</span>}
                                            </button>
                                        ))}
                                        {filteredCompanies.length === 0 && <p className="px-3 py-4 text-sm text-white/40">مفيش نتائج</p>}
                                    </div>
                                </div>

                                <div>
                                    <label className="text-white mb-2 block font-medium text-sm">القطاعات</label>
                                    <div className="flex flex-wrap gap-2">
                                        {SECTOR_OPTIONS.map((s) => (
                                            <button key={s.key} type="button" onClick={() => toggle(selectedSectors, s.key, setSelectedSectors)} className={`text-xs rounded-full px-3 py-1.5 border transition ${selectedSectors.includes(s.key) ? 'bg-purple-500/20 border-purple-400/40 text-white' : 'bg-white/5 border-white/10 text-white/60 hover:text-white'}`}>
                                                {s.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="pt-2">
                                    <button type="submit" disabled={uploading} className="hero-button w-full sm:w-auto inline-flex justify-center rounded-lg py-3 px-10 text-white font-medium ease-in duration-300 hover:opacity-80 disabled:opacity-50 disabled:cursor-wait">
                                        {uploading ? 'جاري الرفع...' : 'رفع الصور'}
                                    </button>
                                    {uploadMsg && <p className="text-green-400 mt-3 text-sm">{uploadMsg}</p>}
                                    {uploadError && <p className="text-red-400 mt-3 text-sm">{uploadError}</p>}
                                </div>
                            </form>
                        </div>

                        {edit && (
                            <div className="bg-[#160a2e] p-6 sm:p-8 rounded-2xl border border-purple-400/30 shadow-lg">
                                <div className="flex items-center justify-between mb-5">
                                    <h2 className="text-white text-xl font-bold">تعديل وسوم الصورة</h2>
                                    <button type="button" onClick={() => setEdit(null)} className="text-sm text-white/60 hover:text-white">إغلاق ✕</button>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                                    <div>
                                        <label className="text-white mb-2 block font-medium text-sm">الوصف</label>
                                        <input type="text" value={edit.label} onChange={(e) => setEdit({ ...edit, label: e.target.value })} className="auth-input w-full rounded-lg p-3 outline-none transition" />
                                    </div>
                                    <div>
                                        <label className="text-white mb-2 block font-medium text-sm">نوع الصورة</label>
                                        <select value={edit.kind} onChange={(e) => setEdit({ ...edit, kind: e.target.value })} className="auth-input w-full rounded-lg p-3 outline-none transition">
                                            <option value="stock">صورة سهم / شركة</option>
                                            <option value="logo">شعار شركة</option>
                                            <option value="sector">صورة قطاع</option>
                                            <option value="general">صورة عامة</option>
                                        </select>
                                    </div>
                                </div>
                                <div className="mb-4">
                                    <label className="text-white mb-2 block font-medium text-sm">الاتجاه</label>
                                    <div className="flex gap-2">
                                        {(['up', 'down', 'neutral'] as const).map((d) => (
                                            <button key={d} type="button" onClick={() => setEdit({ ...edit, direction: d })} className={`rounded-lg py-2 px-4 text-sm font-medium transition ${edit.direction === d ? 'bg-white text-[#100624]' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>
                                                {DIRECTION_LABELS[d]}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="mb-4">
                                    <label className="text-white mb-2 block font-medium text-sm">مرتبطة بـ</label>
                                    <div className="flex flex-wrap gap-2 mb-3">
                                        <button
                                            type="button"
                                            onClick={() => toggle(edit.codes, 'TASI', (v) => setEdit({ ...edit, codes: v }))}
                                            className={`text-xs rounded-full px-3 py-1.5 border transition ${edit.codes.includes('TASI') ? 'bg-purple-500/20 border-purple-400/40 text-white' : 'bg-white/5 border-white/10 text-white/60 hover:text-white'}`}
                                        >
                                            تاسي (المؤشر)
                                        </button>
                                        {edit.codes.filter((c) => c !== 'TASI').map((code) => (
                                            <button key={code} type="button" onClick={() => toggle(edit.codes, code, (v) => setEdit({ ...edit, codes: v }))} className="text-xs rounded-full px-3 py-1.5 bg-purple-500/20 border border-purple-400/40 text-white">
                                                {tagLabel(code)} ✕
                                            </button>
                                        ))}
                                    </div>
                                    <input type="text" value={editQuery} onChange={(e) => setEditQuery(e.target.value)} placeholder="أضف كود سهم بالبحث..." className="auth-input w-full rounded-lg p-3 outline-none transition" />
                                    {editQuery.trim() && (
                                        <div className="mt-2 max-h-40 overflow-y-auto rounded-lg border border-white/10 divide-y divide-white/5">
                                            {editCompanies.map((c) => (
                                                <button key={c.code} type="button" onClick={() => { toggle(edit.codes, c.code, (v) => setEdit({ ...edit, codes: v })); setEditQuery(''); }} className="w-full text-start px-3 py-2 text-sm text-white/70 hover:bg-white/5">
                                                    <span className="text-white/90">{c.name}</span> <span className="text-white/40">({c.code})</span>
                                                </button>
                                            ))}
                                            {editCompanies.length === 0 && <p className="px-3 py-3 text-sm text-white/40">مفيش نتائج</p>}
                                        </div>
                                    )}
                                </div>
                                <div className="mb-5">
                                    <label className="text-white mb-2 block font-medium text-sm">القطاعات</label>
                                    <div className="flex flex-wrap gap-2">
                                        {SECTOR_OPTIONS.map((s) => (
                                            <button key={s.key} type="button" onClick={() => toggle(edit.sectors, s.key, (v) => setEdit({ ...edit, sectors: v }))} className={`text-xs rounded-full px-3 py-1.5 border transition ${edit.sectors.includes(s.key) ? 'bg-purple-500/20 border-purple-400/40 text-white' : 'bg-white/5 border-white/10 text-white/60 hover:text-white'}`}>
                                                {s.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div className="flex items-center gap-4">
                                    <button type="button" onClick={saveEdit} disabled={savingEdit} className="hero-button rounded-lg py-2.5 px-8 text-white font-medium hover:opacity-80 disabled:opacity-50">
                                        {savingEdit ? 'جاري الحفظ...' : 'حفظ الوسوم'}
                                    </button>
                                    {editError && <span className="text-red-400 text-sm">{editError}</span>}
                                </div>
                            </div>
                        )}

                        <div className="bg-[#100624] p-6 sm:p-8 rounded-2xl border border-white/10 shadow-lg">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between mb-6">
                                <h2 className="text-white text-xl font-bold">المكتبة</h2>
                                <div className="flex flex-col sm:flex-row gap-2">
                                    <input type="text" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="بحث بالاسم أو الكود..." className="auth-input rounded-lg p-2.5 text-sm outline-none transition" />
                                    <select value={kindFilter} onChange={(e) => setKindFilter(e.target.value)} className="auth-input rounded-lg p-2.5 text-sm outline-none transition">
                                        <option value="all">كل الأنواع</option>
                                        <option value="stock">صور الأسهم</option>
                                        <option value="logo">الشعارات</option>
                                        <option value="sector">القطاعات</option>
                                        <option value="general">عامة</option>
                                    </select>
                                </div>
                            </div>

                            {libraryLoading && <p className="text-white/50 text-sm">جاري تحميل المكتبة...</p>}
                            {libraryError && <p className="text-red-400 text-sm">{libraryError}</p>}
                            {!libraryLoading && visibleImages.length === 0 && (
                                <div className="text-center py-12 text-white/50">
                                    <p className="font-medium text-white/70 mb-1">المكتبة فاضية</p>
                                    <p className="text-sm">ارفع أول صورة من فوق، والمقالات هتستخدمها لوحدها.</p>
                                </div>
                            )}

                            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                                {visibleImages.map((img) => (
                                    <div key={img.key} className="rounded-xl overflow-hidden border border-white/10 bg-black/20 group">
                                        <div className="relative h-32">
                                            <img src={img.url} alt={img.label} className="w-full h-full object-cover" loading="lazy" />
                                            <span className="absolute top-2 start-2 text-[10px] px-2 py-1 rounded-full bg-black/60 text-white/80">{KIND_LABELS[img.kind] || img.kind}</span>
                                            <span className={`absolute top-2 end-2 text-[10px] px-2 py-1 rounded-full ${img.direction === 'up' ? 'bg-green-500/80' : img.direction === 'down' ? 'bg-red-500/80' : 'bg-white/20'} text-white`}>{DIRECTION_LABELS[img.direction] || img.direction}</span>
                                        </div>
                                        <div className="p-3">
                                            <p className="text-sm text-white/90 truncate" title={img.label}>{img.label}</p>
                                            <p className="text-[11px] text-white/50 truncate">{(img.codes || []).map(tagLabel).join(' · ') || (img.sectors || []).map(sectorLabel).join(' · ') || 'عام'}</p>
                                            <div className="flex items-center justify-between mt-2">
                                                <button type="button" onClick={() => { setImageUrl(img.url); setTab('post'); }} className="text-xs text-purple-300 hover:text-white">استخدم في مقال</button>
                                                <div className="flex items-center gap-3">
                                                    <button type="button" onClick={() => startEdit(img)} className="text-xs text-white/70 hover:text-white">تعديل</button>
                                                    <button type="button" onClick={() => handleDelete(img.key)} className="text-xs text-red-400/80 hover:text-red-300">حذف</button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
                {tab === 'articles' && (
                    <div className="space-y-6">
                        <div className="bg-[#100624] p-6 sm:p-8 rounded-2xl border border-white/10 shadow-lg">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between mb-2">
                                <div>
                                    <h2 className="text-white text-xl font-bold">صور كل مقالات الموقع</h2>
                                    <p className="text-sm text-white/60 mt-1">كل اللي بيتعرض في صفحة الأخبار: المقالات الآلية، المقالات القديمة، الملخص اليومي، والمقالات اليدوية — اربط أو غيّر صورة أي مقال من مكان واحد.</p>
                                </div>
                                <div className="flex gap-2">
                                    <input type="text" value={articleQuery} onChange={(e) => setArticleQuery(e.target.value)} placeholder="ابحث في المقالات..." className="auth-input rounded-lg p-2.5 text-sm outline-none transition" />
                                    <button type="button" onClick={loadArticles} className="rounded-lg px-4 py-2.5 text-sm font-medium bg-white/5 text-white/70 hover:bg-white/10">تحديث</button>
                                </div>
                            </div>
                            {coverMsg && <p className="text-green-400 text-sm mt-3">{coverMsg}</p>}
                        </div>

                        {coverTarget && (
                            <div className="bg-[#160a2e] p-6 sm:p-8 rounded-2xl border border-purple-400/30 shadow-lg">
                                <div className="flex items-center justify-between mb-4">
                                    <h3 className="text-white text-lg font-bold">اختر صورة للمقال{coverArticle ? ` — ${coverArticle.title}` : ''}</h3>
                                    <button type="button" onClick={() => { setCoverTarget(null); setCoverUrl(''); }} className="text-sm text-white/60 hover:text-white">إغلاق ✕</button>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-2 mb-5">
                                    <input type="url" value={coverUrl} onChange={(e) => setCoverUrl(e.target.value)} placeholder="أو الصق رابط صورة (https://...)" className="auth-input flex-1 rounded-lg p-3 text-sm outline-none transition" />
                                    <button type="button" onClick={() => { if (coverArticle) applyCover(coverArticle, coverUrl); }} disabled={coverBusy === coverTarget} className="hero-button rounded-lg px-6 py-3 text-sm font-medium text-white hover:opacity-80 disabled:opacity-50">
                                        {coverBusy === coverTarget ? 'جاري الربط...' : 'اربط الرابط'}
                                    </button>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-6 gap-3">
                                    {images.map((img) => (
                                        <button
                                            type="button"
                                            key={img.key}
                                            onClick={() => { if (coverArticle) applyCover(coverArticle, img.url); }}
                                            disabled={coverBusy === coverTarget}
                                            className="relative rounded-lg overflow-hidden border border-white/10 hover:border-purple-400/60 transition disabled:opacity-50"
                                            title={img.label}
                                        >
                                            <img src={img.url} alt={img.label} className="w-full h-20 object-cover" loading="lazy" />
                                            <span className="absolute bottom-0 inset-x-0 text-[10px] px-2 py-1 bg-black/60 text-white/80 truncate text-start">{(img.codes || []).map(tagLabel).join(' · ') || (img.sectors || []).map(sectorLabel).join(' · ') || 'عام'}</span>
                                        </button>
                                    ))}
                                    {!images.length && <p className="text-sm text-white/50 col-span-full">مكتبة الصور فاضية — ارفع صورة من تاب المكتبة الأول.</p>}
                                </div>
                            </div>
                        )}

                        {articlesLoading && <p className="text-white/50 text-sm px-2">جاري تحميل المقالات...</p>}
                        {articlesError && <p className="text-red-400 text-sm px-2">{articlesError}</p>}

                        <div className="space-y-3">
                            {visibleArticles.map((a) => (
                                <div key={`${a.source}-${a.id}`} className="flex items-center gap-4 bg-[#100624] p-3 sm:p-4 rounded-xl border border-white/10">
                                    <img src={a.image || '/images/news/market.jpg'} alt={a.title} className="w-24 h-16 sm:w-32 sm:h-20 object-cover rounded-lg shrink-0" loading="lazy" />
                                    <div className="min-w-0 flex-1">
                                        <p className="text-white text-sm font-medium line-clamp-2">{a.title}</p>
                                        <p className="text-[11px] text-white/40 mt-1 truncate">{a.slug}{a.date ? ` · ${a.date.slice(0, 10)}` : ''}</p>
                                        <span className="inline-block mt-1 text-[10px] px-2 py-0.5 rounded-full bg-white/10 text-white/70">{SOURCE_LABELS[a.source]}</span>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => { setCoverTarget(a.id); setCoverUrl(''); setCoverMsg(''); }}
                                        className={`shrink-0 rounded-lg px-3 py-2 text-xs font-medium transition ${coverTarget === a.id ? 'bg-white text-[#100624]' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}
                                    >
                                        {a.image ? 'تغيير الصورة' : 'إضافة صورة'}
                                    </button>
                                </div>
                            ))}
                            {!articlesLoading && !visibleArticles.length && (
                                <p className="text-white/50 text-sm px-2">مفيش مقالات مطابقة.</p>
                            )}
                        </div>
                    </div>
                )}
                {tab === 'engine' && (
                    <div className="space-y-6">
                        <div className="bg-[#100624] p-6 sm:p-8 rounded-2xl border border-white/10 shadow-lg">
                            <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
                                <div>
                                    <h2 className="text-white text-xl font-bold">محرك الأخبار الآلي</h2>
                                    <p className="text-sm text-white/60 mt-1">المحرك بيقرأ بيانات السوق والأخبار ويكتب مقالات باسم سارة. من هنا توقفه أو تشغّله، تحدّد الأعداد والمواعيد، وتكتب توجيهاتك.</p>
                                </div>
                                <button type="button" onClick={loadEngine} className="rounded-lg px-4 py-2.5 text-sm font-medium bg-white/5 text-white/70 hover:bg-white/10">تحديث</button>
                            </div>
                            {engineLoading && <p className="text-white/50 text-sm mt-4">جاري قراءة حالة المحرك...</p>}
                            {engineError && <p className="text-red-400 text-sm mt-4">{engineError}</p>}
                            {engine && (
                                <>
                                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mt-5">
                                        <div className="bg-white/5 rounded-xl p-4">
                                            <p className="text-white/50 text-xs">الحالة</p>
                                            <p className={`text-lg font-bold ${engine.config.enabled ? 'text-green-400' : 'text-amber-300'}`}>{engine.config.enabled ? 'شغال' : 'موقوف'}</p>
                                        </div>
                                        <div className="bg-white/5 rounded-xl p-4">
                                            <p className="text-white/50 text-xs">مقالات اليوم ({engine.state.day || engine.today})</p>
                                            <p className="text-lg font-bold text-white">{Object.values(engine.state.counts || {}).reduce((a, b) => a + (b || 0), 0)} / {engine.config.maxPerDay}</p>
                                        </div>
                                        <div className="bg-white/5 rounded-xl p-4">
                                            <p className="text-white/50 text-xs">آخر نشر</p>
                                            <p className="text-sm font-bold text-white">{fmtWhen(engine.state.lastPublishAt)}</p>
                                        </div>
                                        <div className="bg-white/5 rounded-xl p-4">
                                            <p className="text-white/50 text-xs">تكلفة الشهر ({engine.usage.month})</p>
                                            <p className="text-sm font-bold text-white">${Number(engine.usage.thisMonth?.costUsd || 0).toFixed(2)}</p>
                                        </div>
                                    </div>
                                    <div className="mt-3 flex flex-wrap gap-2 text-xs text-white/60">
                                        {Object.keys(ENGINE_LABELS).map((k) => (
                                            <span key={k} className="rounded-full bg-white/5 px-3 py-1">{ENGINE_LABELS[k]}: <b className="text-white/90">{engine.state.counts?.[k] || 0}</b></span>
                                        ))}
                                        <span className="rounded-full bg-white/5 px-3 py-1">إجمالي المقالات: <b className="text-white/90">{engine.total}</b></span>
                                        <span className="rounded-full bg-white/5 px-3 py-1">آخر تشغيل: <b className="text-white/90">{fmtWhen(engine.state.lastRunAt)}</b></span>
                                    </div>
                                    {engine.state.lastError && (
                                        <p className="text-amber-300 text-xs mt-3">
                                            آخر خطأ: {(engine.state.lastError.errors || []).join(' | ')} ({fmtWhen(engine.state.lastError.at)})
                                        </p>
                                    )}
                                </>
                            )}
                        </div>

                        {engineForm && (
                            <form onSubmit={(e) => { e.preventDefault(); saveEngine(); }} className="bg-[#100624] p-6 sm:p-8 rounded-2xl border border-white/10 shadow-lg space-y-5">
                                <h3 className="text-white text-lg font-bold">إعدادات النشر</h3>
                                <div className="flex flex-wrap items-center gap-3">
                                    <button
                                        type="button"
                                        onClick={() => { const next = !engineForm.enabled; setEngineForm({ ...engineForm, enabled: next }); saveEngine({ enabled: next }); }}
                                        disabled={engineSaving}
                                        className={`rounded-lg px-5 py-2.5 text-sm font-medium disabled:opacity-50 ${engineForm.enabled ? 'bg-green-500/15 text-green-300' : 'bg-amber-500/15 text-amber-300'}`}
                                    >
                                        {engineForm.enabled ? 'المحرك شغال — اضغط للإيقاف' : 'المحرك موقوف — اضغط للتشغيل'}
                                    </button>
                                    <p className="text-xs text-white/50">الإيقاف بيوقف النشر التلقائي بس، ومش بيمسح أي مقال.</p>
                                </div>

                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                    <div>
                                        <label className="text-white mb-2 block text-sm font-medium">أقصى مقالات في اليوم</label>
                                        <input type="number" min={1} max={12} value={engineForm.maxPerDay} onChange={(e) => setEngineForm({ ...engineForm, maxPerDay: Number(e.target.value) })} className="auth-input w-full rounded-lg p-3 outline-none transition" />
                                    </div>
                                    <div>
                                        <label className="text-white mb-2 block text-sm font-medium">أخبار الشركات في اليوم</label>
                                        <input type="number" min={0} max={6} value={engineForm.newsPerDay} onChange={(e) => setEngineForm({ ...engineForm, newsPerDay: Number(e.target.value) })} className="auth-input w-full rounded-lg p-3 outline-none transition" />
                                        <p className="text-[11px] text-white/40 mt-1">0 يعني مفيش أخبار شركات.</p>
                                    </div>
                                    <div>
                                        <label className="text-white mb-2 block text-sm font-medium">الفاصل بين أخبار الشركات (دقيقة)</label>
                                        <input type="number" min={10} max={240} value={engineForm.newsGapMin} onChange={(e) => setEngineForm({ ...engineForm, newsGapMin: Number(e.target.value) })} className="auth-input w-full rounded-lg p-3 outline-none transition" />
                                    </div>
                                </div>

                                <div>
                                    <label className="text-white mb-2 block text-sm font-medium">الأنواع المفعّلة</label>
                                    <div className="flex flex-wrap gap-2">
                                        {Object.keys(ENGINE_LABELS).map((k) => {
                                            const off = engineForm.disabledTypes.includes(k);
                                            return (
                                                <button
                                                    key={k}
                                                    type="button"
                                                    onClick={() => toggleEngineType(k)}
                                                    className={`rounded-lg px-4 py-2 text-sm transition ${off ? 'bg-white/5 text-white/40 line-through' : 'bg-purple-500/20 text-purple-200'}`}
                                                >
                                                    {ENGINE_LABELS[k]} · {ENGINE_WINDOWS[k]}
                                                </button>
                                            );
                                        })}
                                    </div>
                                    <p className="text-[11px] text-white/40 mt-2">المضروب عليه بالقلم معناه متوقف — اضغط عليه يفعل تاني.</p>
                                </div>

                                <div>
                                    <label className="text-white mb-2 block text-sm font-medium">توجيهاتك للمحرك (بتتضاف لكل مقال)</label>
                                    <textarea
                                        rows={5}
                                        value={engineForm.instructions}
                                        onChange={(e) => setEngineForm({ ...engineForm, instructions: e.target.value })}
                                        placeholder="مثال: ركّز على قطاع البنوك، اذكر إفصاح الشركة قبل الأرقام، وتجنب أي توصية بيع أو شراء."
                                        className="auth-input w-full rounded-lg p-3 outline-none transition"
                                    />
                                    <p className="text-[11px] text-white/40 mt-2">{engineForm.instructions.length}/1500 حرف.</p>
                                </div>

                                <div className="flex items-center gap-4">
                                    <button type="submit" disabled={engineSaving} className="hero-button rounded-lg px-8 py-3 text-sm font-medium text-white hover:opacity-80 disabled:opacity-50">
                                        {engineSaving ? 'جاري الحفظ...' : 'حفظ الإعدادات'}
                                    </button>
                                </div>
                            </form>
                        )}

                        <div className="bg-[#100624] p-6 sm:p-8 rounded-2xl border border-white/10 shadow-lg">
                            <h3 className="text-white text-lg font-bold">تشغيل فوري</h3>
                            <p className="text-sm text-white/60 mt-1">بيولّد المقال دلوقتي بنفس شروط المحرك، وبيستهلك من رصيد الموديل. مفيد لو فاتت مواعيد النشر أو عايز تجرب نوع معين.</p>
                            <div className="flex flex-wrap gap-2 mt-4">
                                {Object.keys(ENGINE_LABELS).map((k) => (
                                    <button key={k} type="button" onClick={() => runEngine(k)} disabled={!!engineRunning} className="rounded-lg px-4 py-2.5 text-sm font-medium bg-white/5 text-white/80 hover:bg-white/10 disabled:opacity-50">
                                        {engineRunning === k ? 'جاري التشغيل...' : `${ENGINE_LABELS[k]} الآن`}
                                    </button>
                                ))}
                                <button type="button" onClick={() => runEngine()} disabled={!!engineRunning} className="rounded-lg px-4 py-2.5 text-sm font-medium bg-purple-500/20 text-purple-200 hover:bg-purple-500/30 disabled:opacity-50">
                                    {engineRunning === 'all' ? 'جاري التشغيل...' : 'دورة كاملة الآن'}
                                </button>
                            </div>
                            {engineMsg && <p className="text-sm text-white/80 mt-3">{engineMsg}</p>}
                        </div>

                        <div className="bg-[#100624] p-6 sm:p-8 rounded-2xl border border-white/10 shadow-lg space-y-4 text-sm text-white/70 leading-relaxed">
                            <h3 className="text-white text-lg font-bold">إزاي المحرك بيشتغل؟</h3>
                            <div>
                                <p className="text-white/90 font-medium">1) إيه اللي بينشره؟</p>
                                <ul className="list-disc ps-5 mt-1 space-y-1">
                                    <li><b className="text-white/90">قبل الافتتاح</b> — قراءة استعدادية للسوق قبل بداية الجلسة.</li>
                                    <li><b className="text-white/90">خريطة السيولة</b> — فين السيولة بتتحرك وسط الجلسة.</li>
                                    <li><b className="text-white/90">تحليل الجلسة</b> — ملخص الإغلاق: تاسي، القطاعات، والأبرز صعودًا وهبوطًا.</li>
                                    <li><b className="text-white/90">أخبار الشركات</b> — خبر مهم لشركة مدرجة، بإعادة كتابة كاملة باسم سارة وبدون روابط مصادر.</li>
                                </ul>
                            </div>
                            <div>
                                <p className="text-white/90 font-medium">2) امتى بينشر؟</p>
                                <ul className="list-disc ps-5 mt-1 space-y-1">
                                    <li>قبل الافتتاح: 09:20 – 09:58 بتوقيت الرياض.</li>
                                    <li>خريطة السيولة: 12:00 – 13:30.</li>
                                    <li>تحليل الجلسة: 15:30 – 16:40.</li>
                                    <li>أخبار الشركات: 10:00 – 15:20، بحد أقصى 3 يوميًا وبفاصل 50 دقيقة على الأقل.</li>
                                    <li>أحد – خميس فقط، والكرون بيشتغل كل 15 دقيقة داخل المواعيد.</li>
                                </ul>
                            </div>
                            <div>
                                <p className="text-white/90 font-medium">3) منين بيجيب المعلومات؟</p>
                                <ul className="list-disc ps-5 mt-1 space-y-1">
                                    <li>بيانات السوق السعودي (أسعار، تغيّر، سيولة) من ملف البيانات اليومي.</li>
                                    <li>أخبار الشركات من أرشيف الأخبار (آخر 30 ساعة) بعد فلترة الخبر المهم والمرتبط بشركة مدرجة.</li>
                                </ul>
                            </div>
                            <div>
                                <p className="text-white/90 font-medium">4) شروط النشر</p>
                                <ul className="list-disc ps-5 mt-1 space-y-1">
                                    <li>عنوان عربي واضح بدون روابط مصادر أو أسماء وكالات.</li>
                                    <li>الأرقام كلها من بيانات السوق فقط — تاسي منفصل عن نمو والصناديق.</li>
                                    <li>أي شركة تتذكر لازم تكون في قائمة الشركات الرسمية بكودها.</li>
                                    <li>ممنوع تكرار نفس الخبر أو نفس الشركة في نفس اليوم.</li>
                                    <li>حد أقصى 6 مقالات يوميًا إجمالًا + حدود لكل نوع (قابلة للتعديل فوق).</li>
                                    <li>كل مقال باسم سارة — محللة أسواق، السوق السعودي والأمريكي.</li>
                                </ul>
                            </div>
                            <div>
                                <p className="text-white/90 font-medium">5) الصور</p>
                                <p className="mt-1">المحرك بيختار الصورة تلقائيًا: صورة السهم بنفس الاتجاه (صعود/هبوط)، وإلا صورة القطاع، وإلا شعار الشركة، وإلا الغلاف الافتراضي. ولو ربطت صورة لمقال معين من تاب «صور المقالات» بتغلب على الاختيار التلقائي.</p>
                            </div>
                            <div>
                                <p className="text-white/90 font-medium">6) التحكم</p>
                                <p className="mt-1">كل اللي فوق قابل للتغيير من هنا: التشغيل/الإيقاف، الأعداد، الفاصل الزمني، تعطيل نوع بالكامل، وتوجيهات تحريرية بتتضاف لهندسة الكتابة. بعد أي تعديل اضغط «حفظ الإعدادات».</p>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </section>
    );
};

export default CreatePostPage;